import Vue from 'vue'
import { v4 as uuidv4 } from 'uuid'
import registeredWidgets from '@/widgets/registeredWidgets'

const STORAGE_VERSION = 'v1'

const STORAGE_PREFIX = 'dashboard-widgets-'
const ADD_EVENT_NAME = 'addDashboardWidget'
const EDIT_EVENT_NAME = 'editDashboardWidget'
const UPDATE_EVENT_NAME = 'updateDashboardWidget'

export const addWidget = {
  methods: {
    addWidget (component, props) {
      const newWidget = {
        id: uuidv4(),
        component,
        props: {
          ...props,
          controls: true
        }
      }

      const addWidgetEvent = new CustomEvent(ADD_EVENT_NAME, {
        detail: newWidget
      })

      window.dispatchEvent(addWidgetEvent)
    }
  }
}

export const editWidget = {
  mixins: [registeredWidgets],

  methods: {
    /**
     * @param {{
     *   id: string
     *   component: string
     *   props: object
     * }} widget
     */
    editWidget (widget) {
      const editWidgetEvent = new CustomEvent(EDIT_EVENT_NAME, {
        detail: widget
      })
      window.dispatchEvent(editWidgetEvent)
    }
  }
}

export const editingWidget = {
  mixins: [registeredWidgets],

  data () {
    return {
      show: false,
      widgetId: null,
      widgetKey: null,
      settings: {}
    }
  },

  computed: {
    widget () {
      if (!this.widgetKey) {
        return null
      }
      return this.computedRegisteredWidgets[this.widgetKey]
    }
  },

  methods: {
    /**
     * @param {{
     *   id: string
     *   component: string
     *   props: object
     * }} detail
     */
    showWidget ({ detail }) {
      this.settings = Object.assign({}, detail.props)
      this.widgetKey = detail.component
      this.widgetId = detail.id
      this.show = true
    },

    handleEvent (e, settingsKey) {
      this.settings[settingsKey] = e
    },

    updateWidget () {
      const updateWidgetEvent = new CustomEvent(UPDATE_EVENT_NAME, {
        detail: {
          id: this.widgetId,
          component: this.widgetKey,
          props: this.settings
        }
      })

      window.dispatchEvent(updateWidgetEvent)
      this.show = false
    }
  },

  mounted () {
    window.addEventListener(EDIT_EVENT_NAME, this.showWidget)
  },

  beforeDestroy () {
    window.removeEventListener(EDIT_EVENT_NAME, this.showWidget)
  }
}

export const dashboardWidgets = {
  data () {
    return {
      shouldRerender: false,
      dashboardWidgets: null
    }
  },

  mixins: [registeredWidgets],

  computed: {
    widgetsKeys () {
      return Object.keys(this.registeredWidgets)
    },

    availableWidgets () {
      const out = []
      for (const key of this.widgetsKeys) {
        if (!(this.dashboardWidgetsRow.some(w => key === w.component) && this.registeredWidgets[key].unique)) {
          const res = {
            key,
            ...this.registeredWidgets[key]
          }

          if (this.registeredWidgets[key].settings) {
            for (const settingsKey of Object.keys(this.registeredWidgets[key].settings)) {
              res.settings[settingsKey].settingsKey = settingsKey
            }
          }
          out.push(res)
        }
      }
      return out
    },

    availableSettingsRow () {
      const out = {}
      this.availableWidgets.forEach(w => {
        if (w.settings) {
          out[w.key] = {}
          for (const settingsKey of Object.keys(w.settings)) {
            out[w.key][settingsKey] = w.settings[settingsKey].default
          }
        }
      })

      return out
    },

    dashboardWidgetsRow () {
      const widgets = this.dashboardWidgets ? this.dashboardWidgets : {}
      let out = []
      Object.keys(widgets).forEach(key => {
        out = [
          ...out,
          ...widgets[key]
        ]
      })

      return out
    },

    defaultWidgetSet () {
      return {
        col1: [
          {
            id: uuidv4(),
            component: 'DepositBalance',
            props: {
              controls: true
            }
          },
          {
            id: uuidv4(),
            component: 'DashboardActions',
            props: {
              controls: true
            }
          },
          {
            id: uuidv4(),
            component: 'LatestOrders',
            props: {
              controls: true,
              size: 5
            }
          }
        ],
        col2: [
          {
            id: uuidv4(),
            component: 'SummaryWidget',
            props: {
              color: '#0084F8',
              source: 'COMMISSIONS',
              controls: true
            }
          },
          {
            id: uuidv4(),
            component: 'SummaryWidget',
            props: {
              color: '#6C78E6',
              source: 'SALES',
              controls: true
            }
          },
          {
            id: uuidv4(),
            component: 'SummaryWidget',
            props: {
              color: '#B9AE15',
              source: 'GOLDSHOP',
              controls: true
            }
          }
        ],
        col3: [
          {
            id: uuidv4(),
            component: 'RegisteredUsers',
            props: {
              controls: true
            }
          }
        ]
      }
    }
  },

  methods: {
    async deleteWidget (col, index) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dashboardWidgets && this.dashboardWidgets.hasOwnProperty(col)) {
        Vue.delete(this.dashboardWidgets[col], index)
        await this.saveWidgetsState(this.dashboardWidgets)
      }
    },

    getWidgetById (id) {
      return this.dashboardWidgetsRow.find(w => w.id === id)
    },

    async setDefaultWidgets () {
      await this.$storage.set({
        key: STORAGE_PREFIX + STORAGE_VERSION,
        value: JSON.stringify(this.defaultWidgetSet)
      })
    },

    fetchDashboardWidgetsOptions () {},

    async setUserDefinedWidgets () {
      const { value } = await this.$storage.get({ key: STORAGE_PREFIX + STORAGE_VERSION })
      this.dashboardWidgets = JSON.parse(value)
      return value && true
    },

    async insertWidget (e) {
      const newWidget = e.detail

      if (this.dashboardWidgets) {
        const heights = []
        const keys = []
        let col = 1
        for (const key of Object.keys(this.dashboardWidgets)) {
          const el = document.querySelector('div[data-col="' + col + '"]')
          heights.push(el.clientHeight)
          keys.push(key)
          col++
        }
        const minHeight = Math.min(...heights)
        const minKey = keys[heights.findIndex(l => l === minHeight)]
        this.dashboardWidgets[minKey].unshift(newWidget)
        await this.$nextTick()

        const w = document.querySelector('div[data-id="' + newWidget.id + '"]')
        setTimeout(() => {
          w.classList.add('shake')
        }, 400)
        setTimeout(() => {
          w.classList.remove('shake')
        }, 1300)

        await this.saveWidgetsState(this.dashboardWidgets)
      } else {
        throw new Error('Dashboard widgets data prop is not initialized! Check if any errors above')
      }
    },

    async updateWidget (e) {
      const newWidget = e.detail

      if (this.dashboardWidgets) {
        for (const key of Object.keys(this.dashboardWidgets)) {
          const index = this.dashboardWidgets[key].findIndex(widget => newWidget.id === widget.id)

          if (index > -1) {
            Vue.set(this.dashboardWidgets[key], index, newWidget)
          }
        }
        await this.saveWidgetsState(this.dashboardWidgets)
      } else {
        throw new Error('Dashboard widgets data prop is not initialized! Check if any errors above')
      }
    },

    async saveWidgetsState (newState) {
      return this.$storage.set({
        key: STORAGE_PREFIX + STORAGE_VERSION,
        value: JSON.stringify(newState)
      })
    },

    async updateWidgetsState (newState) {
      await this.saveWidgetsState(newState)
      // Vue.set(this, 'dashboardWidgets', newState)
      this.dashboardWidgets = newState
      this.rerender()
      await this.MultipleContainers()
    },

    updateWidgetOptions (widgetId) {},

    /**
     * Rerender is needed to correctly update widget set. If not, there could appear duplicates.
     * Yes, it's a crutch, but it works :)
     */
    rerender () {
      this.shouldRerender = true
      this.$nextTick(() => {
        this.shouldRerender = false
      })
    }
  },

  async beforeMount () {
    const hasSavedWidgets = await this.setUserDefinedWidgets()
    if (!hasSavedWidgets) {
      await this.setDefaultWidgets()
      await this.setUserDefinedWidgets()
    }
  },

  mounted () {
    window.addEventListener(ADD_EVENT_NAME, this.insertWidget)
    window.addEventListener(UPDATE_EVENT_NAME, this.updateWidget)
  },

  beforeDestroy () {
    window.removeEventListener(ADD_EVENT_NAME, this.insertWidget)
    window.removeEventListener(UPDATE_EVENT_NAME, this.updateWidget)
  }
}
